import React, { useEffect, useState, useCallback, useContext } from "react";
import { Progress, Tooltip } from "antd";
import MultiProgress from "react-multi-progress";
import { useParams } from "react-router-dom";
import api from "../axios/api";
import ReactLoading from "react-loading";
import { FaCheckCircle, FaTimesCircle, FaInstagram, FaUser, FaMapMarkerAlt, FaCalendarAlt, FaCity, FaFlag, FaHashtag, FaQrcode, FaPagelines, FaTree, FaClock, FaMapPin, FaDownload, FaTimes, FaFilePdf } from "react-icons/fa";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { EmailShareButton, FacebookShareButton, TwitterShareButton, WhatsappShareButton, LinkedinShareButton, EmailIcon, FacebookIcon, TwitterIcon, WhatsappIcon, LinkedinIcon, } from "react-share";
import QRCode from "qrcode";
import { Modal } from "@mui/material";
import { Line } from "react-chartjs-2";
import { Ruler, DiameterIcon } from "lucide-react";
import { GlobalContext } from "../contexts/GlobalContext";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import "../assets/NewFiles/Css/ProjectPage.css";

// Utility function to safely get data or return fallback
const safeGet = (data, fallback = "") => {
  if (data === null || data === undefined || data === "null" || data === "") {
    return fallback;
  }
  return data;
};

// Utility function to safely format dates
const safeFormatDate = (dateString) => {
  try {
    if (!dateString || dateString === "null" || dateString === null) {
      return "";
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return "";
    }
    return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
  } catch (error) {
    console.error("Error formatting date:", error);
    return "";
  }
};

// Utility function to safely format date with time
const safeFormatDateTime = (dateString) => {
  try {
    if (!dateString || dateString === "null" || dateString === null) {
      return "";
    }
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return "";
    }
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;
    return `${day}/${month}/${year} ${hours}:${minutes} ${ampm}`;
  } catch (error) {
    console.error("Error formatting date time:", error);
    return "";
  }
};

const CO2_TO_O2_RATIO = 0.727; // O2(kg) = CO2e(kg) × 32/44 — mass ratio from photosynthesis stoichiometry (6CO2+6H2O -> C6H12O6 + 6O2)

const calcO2FromCO2 = (co2eKg) => {
  if (!co2eKg || isNaN(co2eKg)) return 0;
  return parseFloat((parseFloat(co2eKg) * CO2_TO_O2_RATIO).toFixed(2));
};

const TreeStages = ({ stagesData, treeInfo, imageUrl }) => {
  // Safe check for stagesData
  if (!stagesData || !stagesData.progress || !Array.isArray(stagesData.progress)) {
    return (
      <div className="mb-6">
        <h2 className="text-xl font-bold mb-3">Tree Growth</h2>
        <p className="text-sm text-gray-700 mb-5">
          Tree journey stages from nursing until maturity
        </p>
        <div className="text-center text-gray-500 py-8">
          No growth data available
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6">
      <h2 className="text-xl font-bold mb-3">Tree Growth</h2>
      <p className="text-sm text-gray-700 mb-5">
        Tree journey stages from nursing until maturity
      </p>

      <div
        id="progress-bar"
        style={{ height: "50px", width: "100%" }}
        className="mt-10"
      >
        <MultiProgress
          transitionTime={1.5}
          height={10}
          elements={stagesData.progress}
        />
      </div>
      <div className="flex flex-wrap justify-between ">
        {stagesData.progress.map((stage, index) => (
          <div key={index} className="flex items-center mr-2 mb-2">
            <span
              style={{
                backgroundColor: stage.color || "#ccc",
                borderRadius: "50%",
                width: "15px",
                height: "15px",
                display: "inline-block",
                marginRight: "5px",
              }}
            ></span>
            <span className="text-xs sm:text-sm text-gray-700">
              {safeGet(stage.label, "Unknown Stage")}
            </span>
          </div>
        ))}
      </div>
      <div className="share-buttons" style={{ justifyContent: "flex-start", margin: '5px' }}>
        <FacebookShareButton url={window.location.href}>
          <FacebookIcon size={32} round />
        </FacebookShareButton>
        <TwitterShareButton url={window.location.href}>
          <TwitterIcon size={32} round />
        </TwitterShareButton>
        <button
          onClick={() => {
            navigator.clipboard
              .writeText(window.location.href)
              .then(() => {
                const shareUrl = `https://www.instagram.com/`;
                window.open(shareUrl, "_blank");
              })
              .catch((err) => {
                console.error("Failed to copy: ", err);
              });
          }}
          style={{
            height: "32px",
            width: "32px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "50%",
            padding: "5px",
            backgroundColor: "#D93F7C",
          }}
        >
          <FaInstagram size={32} />
        </button>
        <WhatsappShareButton url={window.location.href} img={imageUrl} title={safeGet(treeInfo?.treeName, "Tree")} separator={"\n"}>
          <WhatsappIcon size={32} round />
        </WhatsappShareButton>
        <LinkedinShareButton url={window.location.href}>
          <LinkedinIcon size={32} round />
        </LinkedinShareButton>
        <EmailShareButton url={window.location.href}>
          <EmailIcon size={32} round />
        </EmailShareButton>
      </div>
    </div>
  );
};

const Co2OffsetByMonthGraph = ({ treeData = {}, treeHistory = [], chartRef }) => {
  const speciesWD = treeData.bd_biomass_density;
  const speciesFF = treeData.form_factor;

  const buildChartData = () => {
    const points = [];

    const rootDBH = treeData.dia_breast_height;
    const rootDate = treeData.plantedOn || treeData.createdAt;

    if (rootDate && rootDBH != null && rootDBH !== "null" && parseFloat(rootDBH) > 0) {
      points.push({ date: new Date(rootDate), dbh: parseFloat(rootDBH) });
    }

    if (Array.isArray(treeHistory)) {
      treeHistory.forEach((h) => {
        if (
          h.createdAt &&
          h.dia_breast_height != null &&
          h.dia_breast_height !== "null" &&
          parseFloat(h.dia_breast_height) > 0
        ) {
          points.push({
            date: new Date(h.createdAt),
            dbh: parseFloat(h.dia_breast_height),
          });
        }
      });
    }

    const startDate = rootDate ? new Date(rootDate) : null;
    if (!startDate) return null;

    points.sort((a, b) => a.date - b.date);

    const DBH_GROWTH_PER_MONTH = 0.05 / 100;

    const getDBHForDate = (targetDate) => {
      if (points.length === 0) {
        const monthsElapsed = (targetDate - startDate) / (1000 * 60 * 60 * 24 * 30.44);
        const seedlingDBH = 0.005;
        return Math.max(seedlingDBH, seedlingDBH + monthsElapsed * DBH_GROWTH_PER_MONTH);
      }
      if (targetDate <= points[0].date) {
        const monthsBefore = (points[0].date - targetDate) / (1000 * 60 * 60 * 24 * 30.44);
        return Math.max(0.005, points[0].dbh - monthsBefore * DBH_GROWTH_PER_MONTH);
      }
      if (targetDate >= points[points.length - 1].date) {
        const monthsAfter = (targetDate - points[points.length - 1].date) / (1000 * 60 * 60 * 24 * 30.44);
        return points[points.length - 1].dbh + monthsAfter * DBH_GROWTH_PER_MONTH;
      }
      for (let i = 0; i < points.length - 1; i++) {
        if (targetDate >= points[i].date && targetDate <= points[i + 1].date) {
          const t = (targetDate - points[i].date) / (points[i + 1].date - points[i].date);
          return points[i].dbh + t * (points[i + 1].dbh - points[i].dbh);
        }
      }
      return points[points.length - 1].dbh;
    };

    const labels = [];
    const data = [];
    const o2Data = [];
    const isEstimated = [];

    let cursor = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const end = new Date();

    const realSnapshotMonths = new Set(
      points.map(p => `${p.date.getFullYear()}-${p.date.getMonth()}`)
    );

    while (cursor <= end) {
      const label = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`;
      const monthKey = `${cursor.getFullYear()}-${cursor.getMonth()}`;
      const co2eForMonth = calcCO2eFromDBH(getDBHForDate(cursor), speciesWD, speciesFF);
      labels.push(label);
      data.push(co2eForMonth);
      o2Data.push(calcO2FromCO2(co2eForMonth));
      isEstimated.push(!realSnapshotMonths.has(monthKey));
      cursor.setMonth(cursor.getMonth() + 1);
    }

    return {
      labels,
      data,
      o2Data,
      isEstimated,
      snapshotCount: points.length,
      hasRealData: points.length > 0,
    };
  };

  const chartData = buildChartData();

  if (!chartData) {
    return (
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md mb-6 mt-4">
        <h2 className="text-xl font-bold mb-4">Total Cumulative CO2 Offset by Month</h2>
        <p className="text-sm text-gray-500 text-center py-8">
          No planting date available to project CO2e growth
        </p>
      </div>
    );
  }

  const pointColors = chartData.isEstimated.map(est =>
    est ? "rgba(156,163,175,0.6)" : "#4CAF50"
  );
  const pointRadius = chartData.isEstimated.map(est => est ? 2 : 5);

  const o2PointColors = chartData.isEstimated.map(est =>
    est ? "rgba(147,197,253,0.6)" : "#3B82F6"
  );
  const o2PointRadius = chartData.isEstimated.map(est => est ? 2 : 4);

  return (
    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md mb-6 mt-4">
      <h2 className="text-xl font-bold mb-1">CO2 Absorbed &amp; O2 Produced by Month</h2>
      <p className="text-xs text-gray-400 mb-1">
        {chartData.hasRealData
          ? `Based on ${chartData.snapshotCount} real DBH measurement(s) · estimated months grow at +0.05 cm/month · O2 = CO2e × 0.727`
          : "No DBH measurements yet · projected growth at +0.05 cm/month from planting date · O2 = CO2e × 0.727"}
      </p>

      {/* Legend */}
      <div className="flex gap-4 mb-4 flex-wrap">
        <div className="flex items-center gap-1">
          <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#4CAF50', display: 'inline-block' }} />
          <span className="text-xs text-gray-500">CO2e Measured</span>
        </div>
        <div className="flex items-center gap-1">
          <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: 'rgba(156,163,175,0.6)', display: 'inline-block' }} />
          <span className="text-xs text-gray-500">CO2e Estimated</span>
        </div>
        <div className="flex items-center gap-1">
          <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: '#3B82F6', display: 'inline-block' }} />
          <span className="text-xs text-gray-500">O2 Produced</span>
        </div>
      </div>

      <Line
        ref={chartRef}
        data={{
          labels: chartData.labels,
          datasets: [
            {
              label: "CO2e Absorbed (kg)",
              data: chartData.data,
              borderColor: "#4CAF50",
              backgroundColor: "rgba(76, 175, 80, 0.1)",
              fill: true,
              tension: 0.3,
              pointRadius: pointRadius,
              pointBackgroundColor: pointColors,
              pointBorderColor: pointColors,
            },
            {
              label: "O2 Produced (kg)",
              data: chartData.o2Data,
              borderColor: "#3B82F6",
              backgroundColor: "rgba(59, 130, 246, 0.08)",
              fill: true,
              tension: 0.3,
              borderDash: [4, 3],
              pointRadius: o2PointRadius,
              pointBackgroundColor: o2PointColors,
              pointBorderColor: o2PointColors,
            },
          ],
        }}
        options={{
          responsive: true,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  const est = chartData.isEstimated[ctx.dataIndex];
                  const suffix = est ? " (estimated)" : " (measured)";
                  return ` ${ctx.dataset.label}: ${ctx.parsed.y} kg${suffix}`;
                },
              },
            },
          },
          scales: {
            y: {
              beginAtZero: true,
              title: { display: true, text: "kg" },
            },
            x: {
              title: { display: true, text: "Month" },
              ticks: { maxTicksLimit: 12 },
            },
          },
        }}
      />
    </div>
  );
};

const TreeCharacterstics = ({ treeData }) => {
  const characteristics = safeGet(treeData?.characterstrics, "No characteristics data available");

  return (
    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md mb-6">
      <h2 className="text-xl font-bold mb-4">Tree Characteristics</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
        <div style={{ display: "flex" }}>
          <p className="text-sm text-gray-600">{characteristics}</p>
        </div>
      </div>
    </div>
  );
};

const TreeInformation = ({ treeData = {}, treeHistory = [], handleExportPDF, isExporting }) => {
  const [qrUrl, setQrUrl] = useState(null);

  // Find the most recent tree history entry with measurement data
  const getLatestMeasurements = () => {
    if (!Array.isArray(treeHistory) || treeHistory.length === 0) {
      return {
        dia_breast_height: safeGet(treeData.dia_breast_height),
        height: safeGet(treeData.height)
      };
    }

    try {
      // Sort the history by date, newest first
      const sortedHistory = [...treeHistory].sort((a, b) => {
        const dateA = new Date(a.createdAt || 0);
        const dateB = new Date(b.createdAt || 0);
        return dateB - dateA;
      });

      // Find the latest entry with either measurement
      const latestWithDiameter = sortedHistory.find(item =>
        item.dia_breast_height !== null && item.dia_breast_height !== "null" && item.dia_breast_height !== undefined
      );
      const latestWithHeight = sortedHistory.find(item =>
        item.height !== null && item.height !== "null" && item.height !== undefined
      );

      return {
        dia_breast_height: safeGet(latestWithDiameter?.dia_breast_height || treeData.dia_breast_height),
        height: safeGet(latestWithHeight?.height || treeData.height)
      };
    } catch (error) {
      console.error("Error processing tree history:", error);
      return {
        dia_breast_height: safeGet(treeData.dia_breast_height),
        height: safeGet(treeData.height)
      };
    }
  };

  const latestMeasurements = getLatestMeasurements();

  const generateQRCode = async () => {
    try {
      const url = window.location.href;
      const qrCodeDataUrl = await QRCode.toDataURL(url, {
        width: 800,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#ffffff',
        },
      });
      setQrUrl(qrCodeDataUrl);
    } catch (error) {
      console.error("Error generating QR Code", error);
    }
  };

  const downloadQRCode = () => {
    if (qrUrl) {
      const a = document.createElement("a");
      a.href = qrUrl;
      a.download = `${safeGet(treeData.treeName, 'tree')}-qr-code.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  return (
    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md mb-6">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 className="text-xl font-bold" style={{ margin: 0 }}>Tree Information</h2>
        <button
          className="export-pdf-btn"
          onClick={handleExportPDF}
          disabled={isExporting}
          style={{ margin: 0 }}
        >
          <FaFilePdf />{" "}
          <span>
            <strong style={{color:"#0c3292"}}>{isExporting ? "Exporting..." : "Export Report"}</strong>
          </span>
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
        {/* Tree ID */}
        <div className="flex flex-wrap items-start gap-1 mb-2">
          <FaHashtag className="mt-1 min-w-4" />
          <p className="text-sm text-gray-600 font-medium mr-1">Tree ID:</p>
          <p className="text-sm text-gray-600 break-all">{safeGet(treeData.tree_id, "Not specified")}</p>
        </div>

        {/* Uploaded By */}
        <div className="flex flex-wrap items-start gap-1 mb-2">
          <FaUser className="mt-1 min-w-4" />
          <p className="text-sm text-gray-600 font-medium mr-1">Uploaded By:</p>
          <p className="text-sm text-gray-600 break-all">{safeGet(treeData.full_name, "Anonymous")}</p>
        </div>

        {/* Tree Name */}
        <div className="flex flex-wrap items-start gap-1 mb-2">
          <FaTree className="mt-1 min-w-4" />
          <p className="text-sm text-gray-600 font-medium mr-1">Tree Name:</p>
          <p className="text-sm text-gray-600 break-all">{safeGet(treeData.treeName, "Unnamed Tree")}</p>
        </div>

        {/* Verified Status */}
        <div className="flex flex-wrap items-start gap-1 mb-2">
          {treeData.isVerified ? (
            <>
              <FaCheckCircle className="text-green-500 mt-1 min-w-4" />
              <p className="text-sm text-gray-600 font-medium">Verified</p>
            </>
          ) : (
            <>
              <FaTimesCircle className="text-gray-500 mt-1 min-w-4" />
              <p className="text-sm text-gray-600 font-medium">Unverified</p>
            </>
          )}
        </div>

        {/* Diameter at Breast Height */}
        <div className="flex flex-wrap items-start gap-1 mb-2">
          <DiameterIcon className="mt-1 min-w-4" size={18} />
          <p className="text-sm text-gray-600 font-medium mr-1">Diameter at Breast Height:</p>
          <p className="text-sm text-gray-600">
            {latestMeasurements.dia_breast_height ? `${latestMeasurements.dia_breast_height} m` : "Not measured"}
          </p>
        </div>

        {/* Height */}
        <div className="flex flex-wrap items-start gap-1 mb-2">
          <Ruler className="mt-1 min-w-4" size={18} />
          <p className="text-sm text-gray-600 font-medium mr-1">Height:</p>
          <p className="text-sm text-gray-600">
            {latestMeasurements.height ? `${latestMeasurements.height} m` : "Not measured"}
          </p>
        </div>

        {/* Planted On */}
        <div className="flex flex-wrap items-start gap-1 mb-2">
          <FaCalendarAlt className="mt-1 min-w-4" />
          <p className="text-sm text-gray-600 font-medium mr-1">Planted On:</p>
          <p className="text-sm text-gray-600">{safeFormatDate(treeData.plantedOn) || "Date not recorded"}</p>
        </div>

        {/* Age */}
        <div className="flex flex-wrap items-start gap-1 mb-2">
          <FaClock className="mt-1 min-w-4" />
          <p className="text-sm text-gray-600 font-medium mr-1">Age:</p>
          <p className="text-sm text-gray-600">{safeGet(treeData.age, "Unknown")}</p>
        </div>

        {/* Tree Species */}
        <div className="flex flex-wrap items-start gap-1 mb-2">
          <FaPagelines className="mt-1 min-w-4" />
          <p className="text-sm text-gray-600 font-medium mr-1">Tree Species:</p>
          <p className="text-sm text-gray-600 break-all">
            {(() => {
              const species = safeGet(treeData.tree_species);
              const commonName = safeGet(treeData.common_name);
              if ((species && commonName) && (species === "SELECT SPECIES" || commonName === "SELECT SPECIES")) {
                return "Species not identified";
              } else if (species && commonName) {
                return `${species} (${commonName})`;
              } else if (species) {
                return species;
              } else if (commonName) {
                return commonName;
              } else {
                return "Species not identified";
              }
            })()}
          </p>
        </div>

        {/* City */}
        <div className="flex flex-wrap items-start gap-1 mb-2">
          <FaCity className="mt-1 min-w-4" />
          <p className="text-sm text-gray-600 font-medium mr-1">City:</p>
          <p className="text-sm text-gray-600">{safeGet(treeData.city, "Not specified")}</p>
        </div>

        {/* State */}
        <div className="flex flex-wrap items-start gap-1 mb-2">
          <FaFlag className="mt-1 min-w-4" />
          <p className="text-sm text-gray-600 font-medium mr-1">State:</p>
          <p className="text-sm text-gray-600">{safeGet(treeData.state, "Not specified")}</p>
        </div>

        {/* Country */}
        <div className="flex flex-wrap items-start gap-1 mb-2">
          <FaFlag className="mt-1 min-w-4" />
          <p className="text-sm text-gray-600 font-medium mr-1">Country:</p>
          <p className="text-sm text-gray-600">{safeGet(treeData.country, "Not specified")}</p>
        </div>

        {/* Latitude */}
        <div className="flex flex-wrap items-start gap-1 mb-2">
          <FaMapPin className="mt-1 min-w-4" />
          <p className="text-sm text-gray-600 font-medium mr-1">Latitude:</p>
          <p className="text-sm text-gray-600">{safeGet(treeData.lat, "Not available")}</p>
        </div>

        {/* Longitude */}
        <div className="flex flex-wrap items-start gap-1 mb-2">
          <FaMapPin className="mt-1 min-w-4" />
          <p className="text-sm text-gray-600 font-medium mr-1">Longitude:</p>
          <p className="text-sm text-gray-600">{safeGet(treeData.lng, "Not available")}</p>
        </div>

        {/* Address - Full Width */}
        <div className="col-span-1 sm:col-span-2 flex flex-wrap items-start gap-1 mb-2">
          <FaMapMarkerAlt className="mt-1 min-w-4" />
          <p className="text-sm text-gray-600 font-medium mr-1">Address:</p>
          <p className="text-sm text-gray-600 break-all">{safeGet(treeData.address, "Address not provided")}</p>
        </div>

        {/* QR Code Button - Full Width */}
        <div className="col-span-1 sm:col-span-2 mt-2">
          <button
            onClick={generateQRCode}
            className="flex items-center gap-2 px-2 py-2 bg-green-50 hover:bg-green-100 rounded-lg transition-colors duration-200 w-full sm:w-auto"
            title="Generate QR Code"
          >
            <FaQrcode className="text-green-600" size={24} />
            <span className="text-green-600 font-medium">Generate QR Code</span>
          </button>
        </div>
      </div>

      {/* QR Code Modal */}
      <Modal
        open={qrUrl !== null}
        onClose={() => setQrUrl(null)}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-2">
          <div className="bg-white rounded-lg shadow-xl w-11/12 sm:w-3/4 md:w-2/3 lg:w-1/2 max-w-lg mx-auto relative flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-2 sm:p-2 border-b">
              <h3 className="text-base sm:text-lg text-gray-900">Tree QR Code</h3>
              <button
                onClick={() => setQrUrl(null)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors duration-200"
              >
                <FaTimes size={16} className="text-gray-600" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-1 sm:p-2 md:p-3 flex-1 overflow-y-auto">
              <div className="bg-white flex flex-col items-center">
                <div className="text-lg sm:text-xl font-bold text-center text-gray-800 mb-1 sm:mb-2" style={{ color: '#041D5E' }}>
                  {safeGet(treeData.treeName, 'Tree')} QR Code
                </div>

                <div className="text-center space-y-1 w-full px-2 sm:px-4">
                  <p className="text-xs sm:text-sm text-gray-500">Tree ID: {safeGet(treeData.tree_id, "N/A")} </p>
                  <p className="text-xs sm:text-sm text-gray-500 break-all"> {window.location.href}</p>
                </div>

                <div className="w-32 sm:w-40 md:w-48 aspect-square mx-auto">
                  <img
                    src={qrUrl}
                    alt="QR Code"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-2 sm:p-2 flex justify-end border-t">
              <button
                onClick={downloadQRCode}
                className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors duration-200"
              >
                <FaDownload size={14} />
                <span>Download</span>
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const TreeCO2eProgress = ({ treeData = {}, treeHistory = [] }) => {
  const [showInfo, setShowInfo] = useState(false);
  const { isLoggedIn, role } = useContext(GlobalContext);

  const speciesWD = treeData.bd_biomass_density;
  const speciesFF = treeData.form_factor;

  const DBH_GROWTH_PER_MONTH = 0.05 / 100;

  const points = [];
  const rootDBH = treeData.dia_breast_height;
  const rootDate = treeData.plantedOn || treeData.createdAt;

  if (rootDate && rootDBH != null && rootDBH !== "null" && parseFloat(rootDBH) > 0) {
    points.push({ date: new Date(rootDate), dbh: parseFloat(rootDBH) });
  }
  if (Array.isArray(treeHistory)) {
    treeHistory.forEach((h) => {
      if (h.createdAt && h.dia_breast_height != null && h.dia_breast_height !== "null" && parseFloat(h.dia_breast_height) > 0) {
        points.push({ date: new Date(h.createdAt), dbh: parseFloat(h.dia_breast_height) });
      }
    });
  }
  points.sort((a, b) => a.date - b.date);
  const startDate = rootDate ? new Date(rootDate) : null;

  const getDBHForDate = (targetDate) => {
    if (points.length === 0) {
      const monthsElapsed = (targetDate - startDate) / (1000 * 60 * 60 * 24 * 30.44);
      const seedlingDBH = 0.005;
      return Math.max(seedlingDBH, seedlingDBH + monthsElapsed * DBH_GROWTH_PER_MONTH);
    }
    if (targetDate <= points[0].date) {
      const monthsBefore = (points[0].date - targetDate) / (1000 * 60 * 60 * 24 * 30.44);
      return Math.max(0.005, points[0].dbh - monthsBefore * DBH_GROWTH_PER_MONTH);
    }
    if (targetDate >= points[points.length - 1].date) {
      const monthsAfter = (targetDate - points[points.length - 1].date) / (1000 * 60 * 60 * 24 * 30.44);
      return points[points.length - 1].dbh + monthsAfter * DBH_GROWTH_PER_MONTH;
    }
    for (let i = 0; i < points.length - 1; i++) {
      if (targetDate >= points[i].date && targetDate <= points[i + 1].date) {
        const t = (targetDate - points[i].date) / (points[i + 1].date - points[i].date);
        return points[i].dbh + t * (points[i + 1].dbh - points[i].dbh);
      }
    }
    return points[points.length - 1].dbh;
  };

  const today = new Date();
  const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());

  const latestDBH = startDate ? getDBHForDate(today) : null;
  const prevDBH = startDate ? getDBHForDate(lastMonth) : null;

  const currentCO2e = latestDBH ? calcCO2eFromDBH(latestDBH, speciesWD, speciesFF) : 0;
  const prevCO2e = prevDBH ? calcCO2eFromDBH(prevDBH, speciesWD, speciesFF) : 0;
  const monthlyGain = parseFloat(Math.max(0, currentCO2e - prevCO2e).toFixed(2));

  // ── O2 derived directly from the CO2e figures above ──────────────────────
  const currentO2 = calcO2FromCO2(currentCO2e);
  const monthlyO2Gain = calcO2FromCO2(monthlyGain);

  const scaleMax = Math.max(currentCO2e, monthlyGain, 0.01);
  const totalPercent = parseFloat(((currentCO2e / scaleMax) * 100).toFixed(1));
  const gainPercent = parseFloat(((monthlyGain / scaleMax) * 100).toFixed(1));

  const calc = latestDBH ? buildCalcExplanation(latestDBH, speciesWD, speciesFF) : null;

  const usedWD = (speciesWD && parseFloat(speciesWD) > 0)
    ? `${speciesWD} g/cm³ (species-specific)`
    : `${DEFAULT_WOOD_DENSITY} g/cm³ (default — species WD not set)`;

  const usedFF = (speciesFF && parseFloat(speciesFF) > 0)
    ? `${speciesFF} (species-specific)`
    : `${DEFAULT_FORM_FACTOR} (default)`;


  return (
    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
      <div className="flex items-center gap-2 mb-2">
        <h2 className="text-xl font-bold">Tree CO2e Sequestration Progress</h2>
        <button
          onClick={() => setShowInfo(true)}
          title="How is this calculated?"
          className="flex items-center justify-center w-5 h-5 rounded-full bg-green-100 text-green-700 text-xs font-bold hover:bg-green-200 transition-colors"
        >
          i
        </button>
      </div>

      <p className="text-sm text-gray-700 mb-1">
        Estimated CO2e sequestered and O2 produced by this tree to date
      </p>
      {(isLoggedIn && showInfo && role === "admin") ? (<p className="text-xs text-gray-400 mb-4">
        Biomass Formula Method (Chave et al. 2014) ·
        WD: {usedWD} · FF: {usedFF} · O2 = CO2e × 0.727
      </p>) : (<></>)}


      {latestDBH ? (
        <>
          {/* ── Summary numbers ─────────────────────────────────────────── */}
          <div className="flex gap-4 mb-4 flex-wrap">
            <div className="flex flex-col">
              <span className="text-xs text-gray-400">CO2e Stored</span>
              <span className="text-2xl font-bold text-green-600">{currentCO2e} kg</span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-gray-400">CO2e This Month</span>
              <span className="text-2xl font-bold text-blue-500">+{monthlyGain} kg</span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-gray-400">O2 Produced</span>
              <span className="text-2xl font-bold text-sky-600">{currentO2} kg</span>
            </div>
            <div className="flex flex-col">
              <span className="text-xs text-gray-400">O2 This Month</span>
              <span className="text-2xl font-bold text-sky-400">+{monthlyO2Gain} kg</span>
            </div>
          </div>

          {/* ── Progress bars (unchanged — still scaled off CO2e) ────────── */}
          <div className="mb-1 flex items-center gap-2">
            <span style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: "#4CAF50", display: "inline-block", flexShrink: 0 }} />
            <span className="text-xs text-gray-500 w-28 shrink-0">Total till now</span>
            <div className="flex-1">
              <Tooltip title={`${currentCO2e} kg CO2e — ${totalPercent}% of scale`}>
                <Progress percent={totalPercent} strokeColor="#4CAF50" trailColor="#e5e7eb" showInfo={false} size={["100%", 14]} />
              </Tooltip>
            </div>
            <span className="text-xs text-gray-500 w-16 text-right shrink-0">{currentCO2e} kg</span>
          </div>

          <div className="flex items-center gap-2">
            <span style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: "#3B82F6", display: "inline-block", flexShrink: 0 }} />
            <span className="text-xs text-gray-500 w-28 shrink-0">This month</span>
            <div className="flex-1">
              <Tooltip title={`+${monthlyGain} kg CO2e gained this month`}>
                <Progress percent={gainPercent} strokeColor="#3B82F6" trailColor="#e5e7eb" showInfo={false} size={["100%", 14]} />
              </Tooltip>
            </div>
            <span className="text-xs text-blue-500 w-16 text-right shrink-0">+{monthlyGain} kg</span>
          </div>

          <p className="text-xs text-gray-400 mt-3">
            Both bars scale to the larger of the two values - total stored vs this month's gain.
            O2 figures are derived directly from CO2e (x 0.727) and share the same underlying measurement.
          </p>
        </>
      ) : (
        <p className="text-sm text-gray-500 py-4 text-center">
          No DBH measurement available to calculate CO2e
        </p>
      )}

      {/* ── Info Modal ───────────────────────────────────────────────────── */}
      <Modal
        open={showInfo}
        onClose={() => setShowInfo(false)} aria-labelledby="co2e-calc-modal">
        {(isLoggedIn && showInfo && role === "admin") ? (<div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl mx-auto flex flex-col max-h-[90vh]">

            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-bold text-gray-900">How CO2e &amp; O2 are Calculated</h3>
              <button onClick={() => setShowInfo(false)} className="p-1 hover:bg-gray-100 rounded-full">
                <FaTimes size={16} className="text-gray-600" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto text-sm text-gray-700 space-y-4">
              <div>
                <p className="font-semibold text-gray-800 mb-1">Method</p>
                <p>
                  We use the <strong>Biomass Formula Method</strong> based on Chave et al. (2014) —
                  the IPCC-recommended allometric equation for estimating tree Above-Ground Biomass (AGB)
                  from diameter alone. O2 produced is derived from CO2e absorbed using the mass ratio
                  from photosynthesis stoichiometry (6CO2 + 6H2O → C6H12O6 + 6O2).
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-3 space-y-2 font-mono text-xs">
                <p className="font-semibold text-gray-700 font-sans text-sm mb-2">Formula Steps</p>
                <p>① AGB_raw = WD × exp(−1.499 + 2.148·ln(D) + 0.207·ln(D)² − 0.0281·ln(D)³)</p>
                <p>② AGB     = FF × AGB_raw</p>
                <p>③ Carbon  = AGB × 0.47</p>
                <p>④ CO2e    = Carbon × 3.67</p>
                <p>⑤ O2      = CO2e × 0.727  (mass ratio 32/44 from photosynthesis)</p>
              </div>

              <div>
                <p className="font-semibold text-gray-800 mb-1">What the numbers show</p>
                <ul className="space-y-1 text-xs">
                  <li>
                    <span className="inline-block w-3 h-3 rounded-full bg-green-500 mr-1" />
                    <strong>CO2e Stored / This Month</strong> — total and monthly CO2e absorbed
                  </li>
                  <li>
                    <span className="inline-block w-3 h-3 rounded-full bg-sky-500 mr-1" />
                    <strong>O2 Produced / This Month</strong> — total and monthly O2 released, calculated
                    as CO2e × 0.727
                  </li>
                </ul>
              </div>

              <div>
                <p className="font-semibold text-gray-800 mb-1">Variables</p>
                <ul className="space-y-1 text-xs">
                  <li><strong>D</strong> — Diameter at Breast Height in cm</li>
                  <li><strong>WD</strong> — Wood Density (g/cm³), species-specific or 0.5 default</li>
                  <li><strong>FF</strong> — Form Factor, corrects for non-cylindrical trunk (default 0.5)</li>
                  <li><strong>0.47</strong> — Carbon fraction of dry biomass (IPCC)</li>
                  <li><strong>3.67</strong> — Ratio of CO2 molecular weight to Carbon (44÷12)</li>
                  <li><strong>0.727</strong> — Ratio of O2 to CO2 molecular weight (32÷44)</li>
                </ul>
              </div>

              {calc && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 space-y-1 text-xs">
                  <p className="font-semibold text-green-800 text-sm mb-2">
                    Calculation for this tree (DBH = {calc.D} cm)
                  </p>
                  <p>Wood Density (WD) = <strong>{calc.WD} g/cm³</strong></p>
                  <p>Form Factor (FF) = <strong>{calc.FF}</strong></p>
                  <p>ln({calc.D}) = <strong>{calc.lnD}</strong></p>
                  <p>Exponent = −1.499 + 2.148×{calc.lnD} + 0.207×{calc.lnD}² − 0.0281×{calc.lnD}³ = <strong>{calc.exponent}</strong></p>
                  <p>AGB_raw = {calc.WD} × e^{calc.exponent} = <strong>{calc.AGB_raw} kg</strong></p>
                  <p>AGB = {calc.FF} × {calc.AGB_raw} = <strong>{calc.AGB} kg</strong></p>
                  <p>Carbon = {calc.AGB} × 0.47 = <strong>{calc.carbon} kg C</strong></p>
                  <p className="text-green-700 font-bold">CO2e = {calc.carbon} × 3.67 = {calc.co2e} kg CO2e ✓</p>
                  <p className="text-sky-700 font-bold">O2 = {calc.co2e} × 0.727 = {calcO2FromCO2(parseFloat(calc.co2e))} kg O2 ✓</p>
                </div>
              )}

              <div className="text-xs text-gray-400 border-t pt-3">
                <p>
                  <strong>Reference:</strong> Chave et al. (2014) — "Improved allometric models to estimate
                  the aboveground biomass of tropical trees." Global Change Biology, 20(10).
                </p>
              </div>
            </div>
          </div>
        </div>) : (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl mx-auto flex flex-col max-h-[90vh]">

              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-bold text-gray-900">How CO2e Sequestration  &amp; O2 Emission are Calculated</h3>
                <button onClick={() => setShowInfo(false)} className="p-1 hover:bg-gray-100 rounded-full">
                  <FaTimes size={16} className="text-gray-600" />
                </button>
              </div>

              <div className="p-4 overflow-y-auto text-sm text-gray-700 space-y-4">
                <div>
                  <p className="font-semibold text-gray-800 mb-1">Method </p>
                  <p>
                    We use the <strong>Biomass Formula Method</strong> based on Chave et al. (2014)
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────
const DEFAULT_WOOD_DENSITY = 0.5;   // g/cm³ fallback if species WD missing
const DEFAULT_FORM_FACTOR = 0.5;   // standard stem form factor (all NULL in DB)
const MATURITY_CO2E_KG = 1000;  // progress bar 100% target

// ─── Core Formula ────────────────────────────────────────────────────────────
/**
 * Biomass Formula Method — Chave et al. 2014 (no height variant)
 *
 * AGB (kg) = WD × exp( −1.499 + 2.148·ln(D) + 0.207·ln(D)² − 0.0281·ln(D)³ )
 * CO2e (kg) = AGB × carbon_fraction(0.47) × CO2_to_C_ratio(3.67)
 *
 * form_factor adjusts AGB for non-cylindrical stem shape
 *
 * @param {number} dbh_meters  - diameter at breast height in metres
 * @param {number} wd          - wood density in g/cm³
 * @param {number} ff          - form factor (0–1)
 * @returns {number} CO2e in kg, rounded to 2dp
 */
const calcCO2eFromDBH = (dbh_meters, wd = DEFAULT_WOOD_DENSITY, ff = DEFAULT_FORM_FACTOR) => {
  try {
    if (!dbh_meters || isNaN(dbh_meters) || parseFloat(dbh_meters) <= 0) return 0;
    const D = parseFloat(dbh_meters) * 100;   // metres → cm
    const WD = (wd && !isNaN(wd) && parseFloat(wd) > 0) ? parseFloat(wd) : DEFAULT_WOOD_DENSITY;
    const FF = (ff && !isNaN(ff) && parseFloat(ff) > 0) ? parseFloat(ff) : DEFAULT_FORM_FACTOR;
    const lnD = Math.log(D);
    const AGB = FF * WD * Math.exp(
      -1.499 + 2.148 * lnD + 0.207 * lnD ** 2 - 0.0281 * lnD ** 3
    );
    return parseFloat((AGB * 0.47 * 3.67).toFixed(2));
  } catch {
    return 0;
  }
};

/**
 * Builds a human-readable breakdown of the calculation for the info popup
 */
const buildCalcExplanation = (dbh_meters, wd, ff) => {
  const D = parseFloat(dbh_meters) * 100;
  const WD = (wd && parseFloat(wd) > 0) ? parseFloat(wd) : DEFAULT_WOOD_DENSITY;
  const FF = (ff && parseFloat(ff) > 0) ? parseFloat(ff) : DEFAULT_FORM_FACTOR;
  const lnD = Math.log(D);
  const exponent = -1.499 + 2.148 * lnD + 0.207 * lnD ** 2 - 0.0281 * lnD ** 3;
  const AGB_raw = WD * Math.exp(exponent);
  const AGB = FF * AGB_raw;
  const carbon = AGB * 0.47;
  const co2e = carbon * 3.67;
  return {
    D: D.toFixed(2),
    WD, FF,
    lnD: lnD.toFixed(4),
    exponent: exponent.toFixed(4),
    AGB_raw: AGB_raw.toFixed(4),
    AGB: AGB.toFixed(4),
    carbon: carbon.toFixed(4),
    co2e: co2e.toFixed(2),
  };
};


export default function PlantProfile({ mockData = {} }) {
  const { treeId } = useParams();
  const [treeData, setTreeData] = useState({});
  const [treeHistory, setTreeHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxIsOpen, setLightboxIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [isExporting, setIsExporting] = useState(false);

  const co2ChartRef = React.useRef(null);

  const getBase64ImageFromUrl = async (imageUrl) => {
    if (!imageUrl) return null;
    let url = imageUrl;
    if (url.startsWith('/')) {
      url = window.location.origin + url;
    }
    const cacheBuster = (url.includes('?') ? '&' : '?') + '_cb=' + new Date().getTime();
    const fetchUrl = url + cacheBuster;
    try {
      const res = await fetch(fetchUrl, { method: 'GET', mode: 'cors' });
      const blob = await res.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.warn("Fetch failed for image base64, trying fallback:", url, error);
      return new Promise((resolve) => {
        const img = new Image();
        img.setAttribute('crossOrigin', 'anonymous');
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          try {
            resolve(canvas.toDataURL('image/jpeg'));
          } catch (err) {
            console.error("Canvas tainted for image:", url, err);
            resolve(null);
          }
        };
        img.onerror = () => resolve(null);
        img.src = fetchUrl;
      });
    }
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageHeight = doc.internal.pageSize.height;
      const pageWidth = doc.internal.pageSize.width;
      const margin = 15;
      let currentY = 15;

      const addHeader = (titleText) => {
        doc.setFillColor(27, 67, 50);
        doc.rect(margin, currentY, pageWidth - 2 * margin, 12, 'F');
        doc.setFont('Helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(255, 255, 255);
        doc.text(titleText, margin + 5, currentY + 8);
        currentY += 18;
      };

      const checkPageBreak = (heightNeeded) => {
        if (currentY + heightNeeded > pageHeight - margin - 15) {
          doc.addPage();
          currentY = margin;
          return true;
        }
        return false;
      };

      // PAGE 1: HEADER & COVER / MAIN IMAGE & DETAILS
      doc.setFillColor(27, 67, 50);
      doc.rect(margin, currentY, pageWidth - 2 * margin, 25, 'F');

      doc.setFont('Helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(200, 220, 200);
      doc.text('ARANYASETU TREE SPECIFIC IMPACT REPORT', margin + 6, currentY + 8);

      const treeNameText = safeGet(treeData.treeName, "Tree Profile");
      const splitTitle = doc.splitTextToSize(`Tree: ${treeNameText}`, pageWidth - 2 * margin - 12);
      doc.setFontSize(16);
      doc.setTextColor(255, 255, 255);
      doc.text(splitTitle, margin + 6, currentY + 16);
      currentY += 32;

      // Tree Main Image (Centered, adjusted for portrait aspect ratio)
      if (treeData.treeImageUrl) {
        const mainImgBase64 = await getBase64ImageFromUrl(treeData.treeImageUrl);
        if (mainImgBase64) {
          const imgWidth = 80;
          const imgHeight = 90;
          const imgX = (pageWidth - imgWidth) / 2;
          doc.addImage(mainImgBase64, 'JPEG', imgX, currentY, imgWidth, imgHeight, undefined, 'FAST');
          doc.setDrawColor(27, 67, 50);
          doc.setLineWidth(0.3);
          doc.rect(imgX, currentY, imgWidth, imgHeight);
          currentY += imgHeight + 8;
        }
      }

      // Tree Information Table
      const treeSpeciesLabel = (() => {
        const species = safeGet(treeData.tree_species);
        const commonName = safeGet(treeData.common_name);
        if ((species && commonName) && (species === "SELECT SPECIES" || commonName === "SELECT SPECIES")) {
          return "Species not identified";
        } else if (species && commonName) {
          return `${species} (${commonName})`;
        } else if (species) {
          return species;
        } else if (commonName) {
          return commonName;
        } else {
          return "Species not identified";
        }
      })();

      const treeInfoData = [
        ['Tree ID', safeGet(treeData.tree_id, 'N/A')],
        ['Uploaded By', safeGet(treeData.full_name, 'Anonymous')],
        ['Tree Name', safeGet(treeData.treeName, 'Unnamed Tree')],
        ['Tree Species', treeSpeciesLabel],
        ['Diameter at Breast Height', treeData.dia_breast_height ? `${treeData.dia_breast_height} m` : 'Not measured'],
        ['Height', treeData.height ? `${treeData.height} m` : 'Not measured'],
        ['Planted On', safeFormatDate(treeData.plantedOn) || 'Date not recorded'],
        ['Age', safeGet(treeData.age, 'Unknown')],
        ['Location', `${safeGet(treeData.city, '')}, ${safeGet(treeData.state, '')}, ${safeGet(treeData.country, '')}`],
        ['Coordinates', `Lat: ${safeGet(treeData.lat, 'N/A')}, Lng: ${safeGet(treeData.lng, 'N/A')}`],
        ['Full Address', safeGet(treeData.address, 'Address not provided')]
      ];

      autoTable(doc, {
        startY: currentY,
        body: treeInfoData,
        theme: 'striped',
        styles: { fontSize: 9.5, cellPadding: 3, font: 'Helvetica' },
        columnStyles: {
          0: { fontStyle: 'bold', textColor: [27, 67, 50], width: 55 },
          1: { width: 125 }
        },
        margin: { left: margin, right: margin }
      });
      currentY = doc.lastAutoTable.finalY + 15;

      // PAGE 2: SEQUESTRATION PROGRESS & GROWTH STAGES & CHARTS
      // Sequestration calculations
      const speciesWD = treeData.bd_biomass_density;
      const speciesFF = treeData.form_factor;
      const points = [];
      const rootDBH = treeData.dia_breast_height;
      const rootDate = treeData.plantedOn || treeData.createdAt;

      if (rootDate && rootDBH != null && rootDBH !== "null" && parseFloat(rootDBH) > 0) {
        points.push({ date: new Date(rootDate), dbh: parseFloat(rootDBH) });
      }
      if (Array.isArray(treeHistory)) {
        treeHistory.forEach((h) => {
          if (h.createdAt && h.dia_breast_height != null && h.dia_breast_height !== "null" && parseFloat(h.dia_breast_height) > 0) {
            points.push({ date: new Date(h.createdAt), dbh: parseFloat(h.dia_breast_height) });
          }
        });
      }
      points.sort((a, b) => a.date - b.date);
      const startDate = rootDate ? new Date(rootDate) : null;

      const getDBHForDate = (targetDate) => {
        const DBH_GROWTH_PER_MONTH = 0.05 / 100;
        if (points.length === 0) {
          const monthsElapsed = (targetDate - startDate) / (1000 * 60 * 60 * 24 * 30.44);
          const seedlingDBH = 0.005;
          return Math.max(seedlingDBH, seedlingDBH + monthsElapsed * DBH_GROWTH_PER_MONTH);
        }
        if (targetDate <= points[0].date) {
          const monthsBefore = (points[0].date - targetDate) / (1000 * 60 * 60 * 24 * 30.44);
          return Math.max(0.005, points[0].dbh - monthsBefore * DBH_GROWTH_PER_MONTH);
        }
        if (targetDate >= points[points.length - 1].date) {
          const monthsAfter = (targetDate - points[points.length - 1].date) / (1000 * 60 * 60 * 24 * 30.44);
          return points[points.length - 1].dbh + monthsAfter * DBH_GROWTH_PER_MONTH;
        }
        for (let i = 0; i < points.length - 1; i++) {
          if (targetDate >= points[i].date && targetDate <= points[i + 1].date) {
            const t = (targetDate - points[i].date) / (points[i + 1].date - points[i].date);
            return points[i].dbh + t * (points[i + 1].dbh - points[i].dbh);
          }
        }
        return points[points.length - 1].dbh;
      };

      const today = new Date();
      const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());

      const latestDBH = startDate ? getDBHForDate(today) : null;
      const prevDBH = startDate ? getDBHForDate(lastMonth) : null;

      const currentCO2e = latestDBH ? calcCO2eFromDBH(latestDBH, speciesWD, speciesFF) : 0;
      const prevCO2e = prevDBH ? calcCO2eFromDBH(prevDBH, speciesWD, speciesFF) : 0;
      const monthlyGain = parseFloat(Math.max(0, currentCO2e - prevCO2e).toFixed(2));
      const currentO2 = calcO2FromCO2(currentCO2e);
      const monthlyO2Gain = calcO2FromCO2(monthlyGain);

      checkPageBreak(50);
      addHeader('Tree CO2e Sequestration Progress');

      const seqStats = [
        ['Total CO2e Stored till now', `${currentCO2e} kg`],
        ['CO2e Absorbed This Month', `+${monthlyGain} kg`],
        ['Total O2 Produced till now', `${currentO2} kg`],
        ['O2 Emitted This Month', `+${monthlyO2Gain} kg`]
      ];

      autoTable(doc, {
        startY: currentY,
        body: seqStats,
        theme: 'striped',
        styles: { fontSize: 10, cellPadding: 4, font: 'Helvetica' },
        columnStyles: {
          0: { fontStyle: 'bold', textColor: [27, 67, 50], width: 65 },
          1: { width: 115 }
        },
        margin: { left: margin, right: margin }
      });
      const scaleMax = Math.max(currentCO2e, monthlyGain, 0.01);
      const totalPercent = parseFloat(((currentCO2e / scaleMax) * 100).toFixed(1));
      const gainPercent = parseFloat(((monthlyGain / scaleMax) * 100).toFixed(1));

      currentY = doc.lastAutoTable.finalY + 10;
      checkPageBreak(35);

      doc.setFont('Helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(60, 60, 60);

      // Label 1
      doc.text('Total till now:', margin, currentY + 4);

      // Bar 1 background
      doc.setFillColor(229, 231, 235);
      doc.rect(margin + 30, currentY, 100, 6, 'F');

      // Bar 1 value progress (Green)
      doc.setFillColor(76, 175, 80);
      const totalBarWidth = (totalPercent / 100) * 100;
      doc.rect(margin + 30, currentY, totalBarWidth, 6, 'F');

      // Value text 1
      doc.setFont('Helvetica', 'bold');
      doc.text(`${currentCO2e} kg`, margin + 135, currentY + 4);
      currentY += 10;

      // Label 2
      doc.setFont('Helvetica', 'normal');
      doc.text('This month:', margin, currentY + 4);

      // Bar 2 background
      doc.setFillColor(229, 231, 235);
      doc.rect(margin + 30, currentY, 100, 6, 'F');

      // Bar 2 value progress (Blue)
      doc.setFillColor(59, 130, 246);
      const gainBarWidth = (gainPercent / 100) * 100;
      doc.rect(margin + 30, currentY, gainBarWidth, 6, 'F');

      // Value text 2
      doc.setFont('Helvetica', 'bold');
      doc.text(`+${monthlyGain} kg`, margin + 135, currentY + 4);
      currentY += 18;

      // Tree Characteristics if available
      const characteristics = safeGet(treeData.characterstrics);
      if (characteristics && characteristics !== "No characteristics data available") {
        checkPageBreak(35);
        addHeader('Tree Characteristics');

        doc.setFont('Helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(60, 60, 60);
        const splitCharacteristics = doc.splitTextToSize(characteristics, pageWidth - 2 * margin);
        doc.text(splitCharacteristics, margin, currentY);
        currentY += splitCharacteristics.length * 5 + 10;
      }

      // CO2 Absorbed & O2 Produced by Month Chart
      checkPageBreak(95);
      addHeader('CO2 Absorbed & O2 Produced by Month Graph');

      if (co2ChartRef.current) {
        try {
          const co2Img = co2ChartRef.current.toBase64Image();
          if (co2Img) {
            doc.addImage(co2Img, 'PNG', margin, currentY, pageWidth - 2 * margin, 70, undefined, 'FAST');
            currentY += 75;
          }
        } catch (e) {
          console.error("Failed to capture Tree CO2 Chart:", e);
        }
      }

      // Footers
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFont('Helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);

        doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin - 15, pageHeight - 8);
        doc.text(`Aranyasetu Tree ID: ${safeGet(treeData.tree_id, 'N/A')}`, margin, pageHeight - 8);

        doc.setDrawColor(220, 220, 220);
        doc.setLineWidth(0.1);
        doc.line(margin, pageHeight - 11, pageWidth - margin, pageHeight - 11);
      }

      const pdfFileName = `tree_${safeGet(treeData.tree_id, 'profile')}_impact_report.pdf`;
      doc.save(pdfFileName);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to export PDF. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };
  const [images, setImages] = useState([]);
  const [imageDates, setImageDates] = useState([]);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setError(null);
        const response = await api.get(`/api/upload/treeData/${treeId}`);

        // Safe data extraction with fallbacks
        const treeDataResponse = response.data?.treeData?.[0] || {};
        const treeHistoryResponse = response.data?.treeHistory || [];

        setTreeData(treeDataResponse);
        setTreeHistory(Array.isArray(treeHistoryResponse) ? treeHistoryResponse : []);

        // Safe image and date extraction
        const mainImage = treeDataResponse.treeImageUrl;
        const historyImages = Array.isArray(treeHistoryResponse)
          ? treeHistoryResponse.map((hist) => hist?.treeImageUrl).filter(Boolean)
          : [];

        const mainDate = treeDataResponse.createdAt;
        const historyDates = Array.isArray(treeHistoryResponse)
          ? treeHistoryResponse.map((hist) => hist?.createdAt).filter(Boolean)
          : [];

        const allImages = [mainImage, ...historyImages].filter(Boolean);
        const allDates = [mainDate, ...historyDates].filter(Boolean);

        setImages(allImages);
        setImageDates(allDates);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load tree data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    if (treeId) {
      fetchData();
    } else {
      setLoading(false);
      setError("No tree ID provided");
    }
  }, [treeId]);

  const handleImageLoad = useCallback(() => {
    setIsImageLoaded(true);
  }, []);

  useEffect(() => {
    if (lightboxIsOpen && images.length > 0) {
      setIsImageLoaded(false);
      const img = new Image();
      img.src = images[photoIndex];
      img.onload = handleImageLoad;
      img.onerror = () => {
        console.error("Error loading image:", images[photoIndex]);
        setIsImageLoaded(true);
      };
    }
  }, [lightboxIsOpen, photoIndex, images, handleImageLoad]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <ReactLoading type="spin" color="#8BC34A" height={50} width={50} />
        <div style={{ marginLeft: '20px' }}>
          <p>Loading tree profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const treeName = safeGet(treeData.treeName, "Tree");
  const treeAddress = safeGet(treeData.address, "Location not specified");
  const mainImageUrl = safeGet(treeData.treeImageUrl, "/placeholder-tree.jpg");

  return (
    <HelmetProvider>
      <>
        {isExporting && (
          <div className="pdf-export-overlay">
            <div className="pdf-loader-wrapper">
              <ReactLoading type="spin" color="#8BC34A" height={50} width={50} />
            </div>
            <p className="pdf-export-overlay-title">Generating Tree PDF Report...</p>
            <p className="pdf-export-overlay-subtitle">Preparing tree measurements, characteristics, and carbon data</p>
          </div>
        )}
        <Helmet>
          <title>{`${treeName} Profile`}</title>
          <meta name="description" content={safeGet(treeData.tree_id, "Tree Profile")} />

          <meta property="og:type" content="website" />
          <meta property="og:url" content={window.location.href} />
          <meta property="og:title" content={treeName} />
          <meta property="og:description" content={treeAddress} />
          <meta property="og:image" content={mainImageUrl} />

          <meta property="twitter:card" content="summary_large_image" />
          <meta property="twitter:url" content={window.location.href} />
          <meta property="twitter:title" content={treeName} />
          <meta property="twitter:description" content={treeAddress} />
          <meta property="twitter:image" content={mainImageUrl} />

          <meta property="og:site_name" content="Tree Tracking Platform" />
          <meta property="og:image:width" content="300" />
          <meta property="og:image:height" content="300" />
          <meta property="og:image:alt" content={`Image of ${treeName}`} />
        </Helmet>

        <section className="bg-image pt-40 pb-40">
          <div className="container">
            <h2 className="wow fadeInUp" data-wow-duration="1.2s" data-wow-delay=".2s">
              {/* Tree Growth {treeId || ""} */}
              Tree Specific Impact Report
            </h2>
          </div>
        </section>

        <div className="bg-gray-50 min-h-screen py-6 sm:py-10 " >
          <div className="container mx-9 px-4 sm:px-8 w-2/3">
            {/* <div className="tree-climate-report-heading">
              Tree Specific Impact Report
            </div> */}


            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 sm:gap-0">
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <img
                  src={treeData.treeImageUrl}
                  alt="Tree"
                  className="w-70 h-70 rounded-lg shadow-md mb-6 cursor-pointer"
                  onClick={() => {
                    setPhotoIndex(0);
                    setLightboxIsOpen(true);
                  }}
                />
                <div className="flex flex-wrap gap-4 pb-4">
                  {images.map((img, index) => (
                    <div key={index} className="flex flex-col items-center">
                      <img
                        src={img}
                        alt={`Tree history ${index}`}
                        className="w-32 h-32 object-cover rounded cursor-pointer"
                        onClick={() => {
                          setPhotoIndex(index);
                          setLightboxIsOpen(true);
                        }}
                      />
                      <span
                        style={{ color: "black" }}
                        className="text-s mt-2 text-center w-35"
                      >
                        {safeFormatDateTime(imageDates[index])}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <TreeInformation treeData={treeData} treeHistory={treeHistory} handleExportPDF={handleExportPDF} isExporting={isExporting} />
                <TreeCharacterstics treeData={treeData} />
                <TreeCO2eProgress co2Progress={mockData.treeCO2e} treeData={treeData} treeHistory={treeHistory} />
                <Co2OffsetByMonthGraph chartRef={co2ChartRef} treeData={treeData} treeHistory={treeHistory} />
              </div>
            </div>
          </div>
        </div>
        {lightboxIsOpen && (
          <Lightbox
            mainSrc={images[photoIndex]}
            nextSrc={images[(photoIndex + 1) % images.length]}
            prevSrc={images[(photoIndex + images.length - 1) % images.length]}
            onCloseRequest={() => {
              setLightboxIsOpen(false);
              setIsImageLoaded(false);
            }}
            onMovePrevRequest={() => {
              setPhotoIndex((photoIndex + images.length - 1) % images.length);
              setIsImageLoaded(false);
            }}
            onMoveNextRequest={() => {
              setPhotoIndex((photoIndex + 1) % images.length);
              setIsImageLoaded(false);
            }}
            onImageLoad={handleImageLoad}
            loadingElement={
              <div className="flex justify-center items-center h-full">
                <ReactLoading
                  type="spin"
                  color="#8BC34A"
                  height={50}
                  width={50}
                />
              </div>
            }
            imageCaption={`Posted On: ${safeFormatDateTime(imageDates[photoIndex])}`}
            reactModalStyle={{
              overlay: { zIndex: 1500 },
            }}
          />
        )}
      </>
    </HelmetProvider>
  );
}